// StandaloneGamesPanel — side-games UI for casual (non-event) rounds.
//
// Mounted from ScorecardPage when standaloneState.id is set. Everything
// routes through /.netlify/functions/standalone-game (auth = you have
// a round_scores row on the same standalone_round_id).
//
// Deliberately compact — the on-course surface is tight and games are
// glanceable. Live per-game state cards (SkinsCard analogue) render
// inline when a game is expanded; the full detail view is reserved for
// the round leaderboard.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { db } from '../lib/db'
import SkinsCard from './SkinsCard'
import WolfCard from './WolfCard'
import ShuffleCard from './ShuffleCard'
import GameInfoModal from './GameInfoModal'
import { validateSideGameSelection } from '../lib/sideGameValidation'

// Nassau + Match Play are 2-SIDE formats — participant count 2+ is fine,
// team_id splits the sides. Wolf takes 3+ players; the wolf hat rotates
// through the roster (rotation_order = participants). Per-hole Lone
// points scale as wolf ±(N-1) / others ∓1 (zero-sum), Blind is 2× that,
// and Partner is ±1/∓1 per player (not zero-sum for N≠4 — accepted for
// casual play; wagers are per-player anyway).
const FORMAT_OPTIONS = [
  { value: 'skins',        label: 'Skins',        min: 2, max: null, teamed: false, wolf: false, shuffle: false, unitLabel: '$/skin' },
  { value: 'nassau',       label: 'Nassau',       min: 2, max: null, teamed: true,  wolf: false, shuffle: false, unitLabel: '$/segment' },
  { value: 'stableford',   label: 'Stableford',   min: 2, max: null, teamed: false, wolf: false, shuffle: false, unitLabel: '$/round (winner-take-all)' },
  { value: 'simple',       label: 'Simple',       min: 2, max: null, teamed: false, wolf: false, shuffle: false, unitLabel: '$/hole per opponent' },
  { value: 'match_1v1',    label: 'Match Play',   min: 2, max: null, teamed: true,  wolf: false, shuffle: false, unitLabel: '$/match' },
  { value: 'wolf',         label: 'Wolf',         min: 3, max: null, teamed: false, wolf: true,  shuffle: false, unitLabel: '$/point' },
  { value: 'vegas',        label: 'Vegas',        min: 4, max: 4,    teamed: true,  wolf: false, shuffle: false, vegas: true, unitLabel: '$/point' },
  { value: 'shuffle_card', label: 'Shuffle Card', min: 2, max: null, teamed: false, wolf: false, shuffle: true,  wagerless: true, unitLabel: null },
]

async function callStandalone(action, payload) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Please sign in again.')
  const res = await fetch('/.netlify/functions/standalone-game', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json()
  if (!res.ok) {
    const err = new Error(json.error || `${action} failed`)
    err.code = json.code
    throw err
  }
  return json.data
}

export default function StandaloneGamesPanel({
  standaloneRoundId, roundPlayers, viewerPlayerId, viewerCurrentHole,
  onActiveGamesChange,
  // Controlled openForm — the scorecard's "+ Add Game" chip drives the
  // form open/close from outside. When these props are supplied, they
  // become the single source of truth; local openForm becomes an
  // internal mirror only.
  controlledOpenForm,
  onOpenFormChange,
  // hideChrome hides the header + games-list + toast area, leaving only
  // the controlled-form overlay + game-row expansions visible. The
  // scorecard wants the pills-above-scorecard flow (locked with Jacob
  // 2026-07-14) — the box below the group summary shouldn't render.
  hideChrome = false,
  // Holes in this round (18 or 9) — drives the switch-6 vs switch-3 label AND
  // the stored config.holes_intended so switch-6 blocks size to the round, not
  // the course (a front-9 on an 18-hole course still switches every 3).
  holeCount = 18,
}) {
  const vegasSwitchBlock = holeCount <= 9 ? 3 : 6
  // roundPlayers shape from ScorecardPage: [{player_id, name}] — matches
  // the groupPlayers state on the caller (set by setGroupPlayers in the
  // hydration path).
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(true)
  const [localOpenForm, setLocalOpenForm] = useState(false)
  // Prefer the controlled prop when supplied; fall back to local state.
  const openForm = controlledOpenForm !== undefined ? controlledOpenForm : localOpenForm
  const setOpenForm = (v) => {
    const next = typeof v === 'function' ? v(openForm) : v
    if (onOpenFormChange) onOpenFormChange(next)
    else setLocalOpenForm(next)
  }
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState(null)
  const [expandedGame, setExpandedGame] = useState(null)

  // Create-form state
  const [format, setFormat] = useState('skins')
  const [unitValue, setUnitValue] = useState('1')
  // Default the participant list to everyone on the round. The common
  // case is "everyone in the group is in the game"; deselecting the odd
  // spectator is less input than clicking every player. Refreshes when
  // roundPlayers changes (e.g. a player is added mid-round).
  const [selectedPlayers, setSelectedPlayers] = useState(new Set())
  useEffect(() => {
    if (!openForm) return
    setSelectedPlayers(new Set(roundPlayers.map(rp => rp.player_id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openForm, roundPlayers.length])
  // Team assignment for Nassau / Match Play — {player_id: 'A' | 'B'}.
  // Ignored server-side for non-teamed formats.
  const [teamAssignment, setTeamAssignment] = useState(new Map())
  // Wolf rotation config. rotation_mode: 'scorecard' (participants array
  // order — default), 'auto' (random on server), 'set' (drag-order below).
  // rotationOrder is the ordered array of 4 player_ids used when
  // rotation_mode === 'set'.
  const [wolfRotationMode, setWolfRotationMode] = useState('scorecard')
  const [wolfRotationOrder, setWolfRotationOrder] = useState([])
  // Shuffle Card mode picker — Straight Up / Double Down / Duel. Locked
  // after the first card is drawn (server-side check + Edit gate).
  const [shuffleMode, setShuffleMode] = useState('straight_up')
  // Vegas flip toggle — default ON (opponent natural birdie flips your
  // number high; a natural eagle also doubles the hole).
  const [vegasFlip, setVegasFlip] = useState(true)
  // Vegas teams mode — 'same' (fixed 2v2, manual picker) or 'switch6' (server
  // rotates the pairing every 6 holes / every 3 on a 9-hole round; no picker).
  const [vegasTeamsMode, setVegasTeamsMode] = useState('same')
  // Vegas ties — 'wash' (tie pays nobody) or 'push' (tie carries a growing
  // multiplier onto the next decided hole).
  const [vegasTiesMode, setVegasTiesMode] = useState('wash')
  // Vegas presses — only valid with fixed teams ('same').
  const [vegasAllowPress, setVegasAllowPress] = useState(false)
  // Edit affordance state — { game_id, config } when editing an existing
  // game inline; null when the panel is in create/idle mode. Reuses the
  // same form fields as create with prefill from the game's current config.
  const [editingGame, setEditingGame] = useState(null)
  // Which format's "How to play" popup is open. null = closed.
  const [infoFormat, setInfoFormat] = useState(null)
  // F-094-T1: stub-birthdate ack. Required when creating a wager-enabled
  // game (unit > 0 on a wagered format) — casual rounds typically include
  // Player 2/3/4 stubs without birthdates, so the organizer confirms 18+
  // offline. Server-enforced check ONLY blocks players whose birthdate is
  // affirmatively under 18; this ack captures the offline confirmation
  // for the unknown-birthdate case + writes an audit trail on games.config.
  const [stubAgeAck, setStubAgeAck] = useState(false)
  const currentFormat = FORMAT_OPTIONS.find(f => f.value === format) || FORMAT_OPTIONS[0]
  // Wager-enabled = a money game (unit > 0 on a wagered format). Wagerless
  // formats (Shuffle Card) are open regardless.
  const wagerEnabled = !currentFormat.wagerless && (Number(unitValue) || 0) > 0
  // roundPlayers carries {player_id, name} today — no birthdate signal —
  // so we conservatively require the ack for ANY wager-enabled create with
  // participants (casual rounds are stub-heavy). See F-094-T1 spec risk 3.
  const stubAckRequired = wagerEnabled && selectedPlayers.size > 0

  // For teamed formats, keep A / B counts up to date; auto-assign a
  // sensible default when exactly 2 players are selected without any
  // existing assignments.
  const teamCounts = useMemo(() => {
    let a = 0, b = 0
    for (const pid of selectedPlayers) {
      const t = teamAssignment.get(pid)
      if (t === 'A') a += 1
      else if (t === 'B') b += 1
    }
    return { a, b }
  }, [selectedPlayers, teamAssignment])

  // Keep wolfRotationOrder in sync with the current player selection
  // (any size ≥3) whenever we're in 'set' mode. Otherwise the order
  // picker renders no rows (nothing to iterate) or shows a stale order.
  useEffect(() => {
    if (wolfRotationMode !== 'set') return
    if (selectedPlayers.size < 3) {
      if (wolfRotationOrder.length !== 0) setWolfRotationOrder([])
      return
    }
    const sel = Array.from(selectedPlayers)
    const same = wolfRotationOrder.length === sel.length
      && wolfRotationOrder.every(id => sel.includes(id))
      && sel.every(id => wolfRotationOrder.includes(id))
    if (!same) setWolfRotationOrder(sel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wolfRotationMode, selectedPlayers])

  const showToast = useCallback((msg) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }, [])

  const loadGames = useCallback(async () => {
    if (!standaloneRoundId) return
    setLoading(true)
    try {
      const data = await callStandalone('list_games', { standalone_round_id: standaloneRoundId })
      setGames(data || [])
    } catch (e) {
      // Migration-pending / auth error → surface once, then leave the
      // panel with an empty list so the round-view isn't blocked.
      if (e.code !== 'standalone_games_migration_pending') {
        showToast(e.message)
      }
      setGames([])
    }
    setLoading(false)
  }, [standaloneRoundId, showToast])

  useEffect(() => { loadGames() }, [loadGames])

  const togglePlayer = (pid) => {
    setSelectedPlayers(prev => {
      const next = new Set(prev)
      if (next.has(pid)) next.delete(pid); else next.add(pid)
      return next
    })
  }

  const setTeam = (pid, team) => {
    setTeamAssignment(prev => {
      const next = new Map(prev)
      if (next.get(pid) === team) next.delete(pid) // toggle off
      else next.set(pid, team)
      return next
    })
  }

  // Randomize the 4 selected players into a fresh 2v2 (Vegas fixed-teams).
  const randomizeVegasTeams = () => {
    const ids = Array.from(selectedPlayers)
    if (ids.length !== 4) return
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[ids[i], ids[j]] = [ids[j], ids[i]]
    }
    const next = new Map()
    ids.forEach((pid, i) => next.set(pid, i < 2 ? 'A' : 'B'))
    setTeamAssignment(next)
  }

  const createGame = async () => {
    const n = selectedPlayers.size
    if (n < currentFormat.min) { showToast(`${currentFormat.label} needs at least ${currentFormat.min} players`); return }
    if (currentFormat.max && n !== currentFormat.max) {
      showToast(`${currentFormat.label} needs exactly ${currentFormat.max} players`); return
    }
    // Exempt Vegas here — it has its own guard below (and, in switch6 mode,
    // the server seeds the 2v2 so team_ids are legitimately absent). Live
    // evidence 2026-07-19: this guard fired for a valid switch6 4-player
    // create and toasted "Assign every player to Team A or Team B" even
    // though the picker is hidden by design in switch6 mode.
    if (currentFormat.teamed && !currentFormat.vegas && n > 2) {
      if (teamCounts.a === 0 || teamCounts.b === 0) {
        showToast('Assign every player to Team A or Team B before starting.')
        return
      }
    }
    if (currentFormat.vegas && vegasTeamsMode === 'same' && (teamCounts.a !== 2 || teamCounts.b !== 2)) {
      showToast('Vegas needs two teams of exactly two — assign 2 players to A and 2 to B.')
      return
    }
    // F-094-T1: enforce the stub-age ack when needed. Server also gates on
    // birthdate-known minors so this is UX/audit only — but it's a hard
    // gate here so the round admin can't ship a wager without confirming.
    if (stubAckRequired && !stubAgeAck) {
      showToast('Confirm every participant is 18+ before starting a wager game.')
      return
    }
    const unit = Number(unitValue) || 0
    let config
    switch (format) {
      case 'skins':      config = { scoring_mode: 'gross', carry_from_ties: true, unit_value: unit }; break
      case 'nassau':     config = { bet_units: { front: unit, back: unit, overall: unit } }; break
      case 'stableford':
      case 'simple':     config = { unit_value: unit }; break
      case 'match_1v1':  config = { bet_units: unit }; break
      case 'vegas':      config = {
        unit_value: unit, flip_enabled: vegasFlip, scoring_mode: 'gross',
        teams_mode: vegasTeamsMode,
        ties_mode: vegasTiesMode,
        allow_press: vegasTeamsMode === 'same' ? vegasAllowPress : false,
        // Intended round length so switch-6 blocks size to the round (three
        // 6-hole blocks on 18, three 3-hole blocks on 9), not the course size.
        // Read server-side by resolveVegasSwitchHoles.
        holes_intended: holeCount <= 9 ? 9 : 18,
      }; break
      case 'wolf': {
        // rotation_order semantics per rotation_mode:
        //   scorecard → participant order (selectedPlayers iteration order
        //                is insertion order for JS Sets, which mirrors the
        //                roundPlayers array the user clicked into)
        //   set       → wolfRotationOrder (drag-set below)
        //   auto      → server randomizes when we omit / mark
        const sel = Array.from(selectedPlayers)
        let rotation_order = sel
        if (wolfRotationMode === 'set' && wolfRotationOrder.length === sel.length) {
          rotation_order = wolfRotationOrder
        }
        config = {
          unit_value: unit,
          rotation_mode: wolfRotationMode,
          rotation_order,
          blind_wolf_enabled: true,
        }
        break
      }
      case 'shuffle_card':
        // Shuffle mode from the create-form radio picker; can be edited
        // later via the Edit affordance IF no card has been drawn yet
        // (server-side check gates the switch — see update_game_config).
        // Wagerless — Shuffle stacks under Skins / Nassau for money
        // games; the mechanic IS the game here.
        config = {
          shuffle_mode: shuffleMode,
          index_eligible_opt_in: false,
        }
        break
      default:           config = { unit_value: unit }
    }
    // F-094-T1: forward the ack flag to the server. Server rewrites
    // `true` → `{ by: caller.id, at: iso }` on the persisted config for
    // the audit trail; ack is only present for wager-enabled games.
    if (stubAckRequired && stubAgeAck) {
      config.stub_age_ack = true
    }
    // Payload is [{player_id, team_id}]. team_id is null for non-teamed
    // formats; server ignores it there. For 2-player teamed with no
    // explicit split, the server treats the two as their own sides
    // (legacy 1v1 behavior).
    const payload = Array.from(selectedPlayers).map(pid => ({
      player_id: pid,
      team_id: currentFormat.vegas
        // switch6 → server seeds membership from the rotation, send null.
        ? (vegasTeamsMode === 'switch6' ? null : (teamAssignment.get(pid) || null))
        : (currentFormat.teamed ? (teamAssignment.get(pid) || null) : null),
    }))
    setBusy(true)
    try {
      const created = await callStandalone('create_game', {
        standalone_round_id: standaloneRoundId,
        format,
        config,
        participants: payload,
      })
      showToast(`${currentFormat.label} started.`)
      setOpenForm(false)
      setSelectedPlayers(new Set())
      setTeamAssignment(new Map())
      setWolfRotationMode('scorecard')
      setWolfRotationOrder([])
      setVegasFlip(true)
      setVegasTeamsMode('same')
      setVegasTiesMode('wash')
      setVegasAllowPress(false)
      setStubAgeAck(false)
      await loadGames()
      // Auto-expand the just-created game so the user doesn't have to
      // tap View. The server returns { game_id, format }.
      if (created?.game_id) setExpandedGame(created.game_id)
    } catch (e) {
      showToast(e.message)
    }
    setBusy(false)
  }

  const endGame = async (game_id) => {
    if (!window.confirm('End this game? Final points get locked in and the recap stays visible on the round detail page.')) return
    setBusy(true)
    try {
      await callStandalone('end_game', { game_id })
      await loadGames()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  // F-094-T1 (review fix): mid-round add / remove participant on a
  // standalone game. Mirrors the EventDraftAdmin add/remove affordance
  // so the server-side add_participant under-18 wager gate at
  // netlify/functions/standalone-game.js:321 is reachable from the app.
  // Errors (including under_18_wager_block) surface via the shared
  // toast so the owner sees the block message without opening devtools.
  const addParticipant = async (game_id, player_id) => {
    if (!player_id) return
    setBusy(true)
    try {
      await callStandalone('add_participant', { game_id, player_id })
      await loadGames()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const removeParticipant = async (game_id, player_id) => {
    setBusy(true)
    try {
      await callStandalone('remove_participant', { game_id, player_id })
      await loadGames()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const activeGames = useMemo(() => games.filter(g => !g.ended_at), [games])

  // Notify parent of the active-games list so the scorecard can render
  // inline surfaces for stateful games (Shuffle Card's 4-card picker,
  // Dots achievement toggles, etc.) without redoing the same list_games
  // fetch. Sends a minimal projection — id + format only — so the parent
  // effect fires only on real change.
  useEffect(() => {
    if (!onActiveGamesChange) return
    onActiveGamesChange(activeGames.map(g => ({ id: g.id, format: g.format })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGames.length, activeGames.map(g => g.id).join('|')])

  if (!standaloneRoundId) return null
  if (loading && games.length === 0) return null

  // hideChrome variant (Scorecard v2 layout — pills above scorecard drive
  // the flow; the box below is gone). Outer container drops border +
  // background + header. The form renders as a modal-style overlay,
  // still driven by the same `openForm` flag (now typically controlled
  // by the scorecard's "+ Add Game" chip). The games list still renders
  // for Edit/End of formats that don't have dedicated pages yet
  // (Wolf/Skins) but as bare cards, no wrapper. Shuffle rows still render
  // for the panel-based Edit affordance until it moves fully to /shuffle.
  const outerStyle = hideChrome
    ? { marginTop: 0, padding: 0, border: 'none', background: 'transparent' }
    : {
        marginTop: 20, padding: 12,
        border: '1px solid var(--green-mid)',
        borderRadius: 'var(--radius)',
        background: 'var(--green-deep)',
      }
  const formWrapperStyle = hideChrome
    ? {
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }
    : { borderTop: '1px solid var(--green-mid)', paddingTop: 10, marginBottom: 10 }
  const formInnerStyle = hideChrome
    ? {
        maxWidth: 400, width: '100%',
        maxHeight: '80vh', overflowY: 'auto',
        padding: 16, borderRadius: 10,
        background: 'var(--green-deep)',
        border: '1px solid var(--gold)',
      }
    : {}
  return (
    <div style={outerStyle}>
      {!hideChrome && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Side games</h3>
          <button
            onClick={() => setOpenForm(o => !o)}
            style={{
              padding: '6px 12px', border: '1px solid var(--gold)',
              background: 'transparent', color: 'var(--gold)',
              borderRadius: 6, fontSize: '0.75rem', cursor: 'pointer',
            }}
          >
            {openForm ? 'Cancel' : '+ New game'}
          </button>
        </div>
      )}

      {openForm && (
        <div style={formWrapperStyle} onClick={hideChrome ? (() => setOpenForm(false)) : undefined}>
        <div style={formInnerStyle} onClick={hideChrome ? (e => e.stopPropagation()) : undefined}>
        {hideChrome && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--gold)' }}>Add a side game</h3>
            <button
              onClick={() => setOpenForm(false)}
              aria-label="Close"
              style={{
                background: 'none', border: 'none', color: 'var(--gray-300)',
                fontSize: '1.4rem', cursor: 'pointer',
              }}
            >×</button>
          </div>
        )}
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 4px' }}>Format</p>
          <div style={{ display: 'flex', gap: 6, alignItems: 'stretch', marginBottom: 10 }}>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              style={{
                flex: 1, padding: 8, borderRadius: 6,
                background: 'var(--green-dark)', color: 'var(--cream)',
                border: '1px solid var(--green-mid)', fontSize: '0.85rem',
              }}
            >
              {FORMAT_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>
                  {f.label}{f.teamed ? ' — 2 sides' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setInfoFormat(format)}
              aria-label={`How ${currentFormat.label} works`}
              title={`How ${currentFormat.label} works`}
              style={{
                flex: 'none', padding: '0 12px', borderRadius: 6,
                border: '1px solid var(--green-mid)',
                background: 'transparent', color: 'var(--gold)',
                fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
              }}
            >ⓘ How to play</button>
          </div>

          {!currentFormat.wagerless && (
            <>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 4px' }}>
                {currentFormat.unitLabel} (0 = bragging rights only)
              </p>
              <input
                type="number" min={0} step="0.25"
                value={unitValue}
                onChange={(e) => setUnitValue(e.target.value)}
                style={{
                  width: '100%', padding: 8, marginBottom: 4, borderRadius: 6,
                  background: 'var(--green-dark)', color: 'var(--cream)',
                  border: '1px solid var(--green-mid)', fontSize: '0.85rem',
                }}
              />
              <p style={{ fontSize: '0.7rem', color: 'var(--gray-500)', margin: '0 0 10px' }}>
                Money amounts are display-only. TournyFlex never handles wager funds.
              </p>
            </>
          )}
          {currentFormat.wagerless && (
            <p style={{ fontSize: '0.72rem', color: 'var(--gray-500)', margin: '0 0 10px', lineHeight: 1.4 }}>
              {currentFormat.label} is a wagerless game — the mechanic IS the game.
              Stack Skins or Nassau alongside if the group wants money on the round.
            </p>
          )}

          {currentFormat.wolf && (
            <div style={{
              marginBottom: 10, padding: 8, borderRadius: 6,
              background: 'rgba(0,0,0,0.15)', border: '1px solid var(--green-mid)',
            }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 6px' }}>
                Rotation order (who's Wolf on hole 1, hole 2, and so on):
              </p>
              <div style={{ display: 'grid', gap: 4, marginBottom: 6 }}>
                {[
                  { v: 'scorecard', label: 'Use scorecard order' },
                  { v: 'auto',      label: 'Choose for me (random)' },
                  { v: 'set',       label: 'Set the order myself' },
                ].map(o => (
                  <label key={o.v} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={wolfRotationMode === o.v}
                      onChange={() => {
                        setWolfRotationMode(o.v)
                        if (o.v === 'set' && selectedPlayers.size === 4) {
                          setWolfRotationOrder(Array.from(selectedPlayers))
                        } else if (o.v !== 'set') {
                          setWolfRotationOrder([])
                        }
                      }}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
              {wolfRotationMode === 'set' && selectedPlayers.size === 4 && (
                <div style={{ marginTop: 6 }}>
                  <p style={{ fontSize: '0.7rem', color: 'var(--gray-400)', margin: '0 0 4px' }}>Tap ↑↓ to reorder:</p>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {wolfRotationOrder.map((pid, i) => {
                      const name = roundPlayers.find(rp => rp.player_id === pid)?.name || pid.slice(0, 6)
                      return (
                        <div key={pid} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '4px 8px', borderRadius: 4,
                          background: 'var(--green-dark)', border: '1px solid var(--green-mid)',
                          fontSize: '0.8rem',
                        }}>
                          <span style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>{i + 1}.</span>
                          <span style={{ flex: 1 }}>{name}</span>
                          <button
                            disabled={i === 0}
                            onClick={() => {
                              const next = [...wolfRotationOrder]
                              ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
                              setWolfRotationOrder(next)
                            }}
                            style={miniBtn}
                          >↑</button>
                          <button
                            disabled={i === 3}
                            onClick={() => {
                              const next = [...wolfRotationOrder]
                              ;[next[i], next[i + 1]] = [next[i + 1], next[i]]
                              setWolfRotationOrder(next)
                            }}
                            style={miniBtn}
                          >↓</button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {wolfRotationMode === 'set' && selectedPlayers.size !== 4 && (
                <p style={{ fontSize: '0.7rem', color: 'var(--gray-400)', margin: '4px 0 0' }}>
                  Pick 4 players first, then set the order here.
                </p>
              )}
            </div>
          )}

          {currentFormat.shuffle && (
            <div style={{
              marginBottom: 10, padding: 8, borderRadius: 6,
              background: 'rgba(0,0,0,0.15)', border: '1px solid var(--green-mid)',
            }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 6px' }}>
                Mode:
              </p>
              <div style={{ display: 'grid', gap: 4 }}>
                {[
                  { v: 'straight_up', label: 'Straight Up', desc: '1 card per group per hole' },
                  { v: 'double_down', label: 'Double Down', desc: '2 cards per group per hole; effects stack' },
                  { v: 'duel',        label: 'Duel',        desc: '2 sides, shared deck; each side picks its own card' },
                ].map(m => (
                  <label key={m.v} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={shuffleMode === m.v}
                      onChange={() => setShuffleMode(m.v)}
                      style={{ marginTop: 2 }}
                    />
                    <span style={{ flex: 1 }}>
                      <span style={{ fontWeight: 600 }}>{m.label}</span>
                      <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.68rem' }}>{m.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {currentFormat.vegas && (
            <div style={{
              marginBottom: 10, padding: 8, borderRadius: 6,
              background: 'rgba(0,0,0,0.15)', border: '1px solid var(--green-mid)',
              display: 'grid', gap: 10,
            }}>
              {/* Teams mode */}
              <div>
                <p style={{ fontSize: '0.72rem', color: 'var(--gray-300)', margin: '0 0 4px', fontWeight: 600 }}>Teams</p>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[
                    { v: 'same', label: 'Same all round' },
                    { v: 'switch6', label: `Switch every ${vegasSwitchBlock}` },
                  ].map(m => (
                    <button
                      key={m.v} type="button"
                      onClick={() => { setVegasTeamsMode(m.v); if (m.v === 'switch6') setVegasAllowPress(false) }}
                      style={sgChip(vegasTeamsMode === m.v)}
                    >{m.label}</button>
                  ))}
                </div>
                <p style={{ fontSize: '0.66rem', color: 'var(--gray-500)', margin: '4px 0 0' }}>
                  {vegasTeamsMode === 'switch6'
                    ? `Partners rotate every ${vegasSwitchBlock} holes across three pairings. No picker — teams are randomized.`
                    : 'Same partners all round — assign the 2v2 below.'}
                </p>
                {vegasTeamsMode === 'same' && selectedPlayers.size === 4 && (
                  <button
                    type="button"
                    onClick={randomizeVegasTeams}
                    style={{ ...sgChip(false), marginTop: 6, flex: 'none', padding: '4px 10px' }}
                  >🎲 Randomize teams</button>
                )}
              </div>

              {/* Ties mode */}
              <div>
                <p style={{ fontSize: '0.72rem', color: 'var(--gray-300)', margin: '0 0 4px', fontWeight: 600 }}>Ties</p>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[
                    { v: 'wash', label: 'Wash' },
                    { v: 'push', label: 'Push (carries & doubles the next hole)' },
                  ].map(m => (
                    <button
                      key={m.v} type="button"
                      onClick={() => setVegasTiesMode(m.v)}
                      style={sgChip(vegasTiesMode === m.v)}
                    >{m.label}</button>
                  ))}
                </div>
              </div>

              {/* Allow press — fixed teams only */}
              {vegasTeamsMode === 'same' && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={vegasAllowPress}
                    onChange={(e) => setVegasAllowPress(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>Allow Press</span>
                    <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.68rem' }}>
                      The losing team can double a single hole. Only the team behind sees the button on an un-scored hole.
                    </span>
                  </span>
                </label>
              )}

              {/* Flip */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={vegasFlip}
                  onChange={(e) => setVegasFlip(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600 }}>Flip on natural birdie</span>
                  <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.68rem' }}>
                    When your opponents make a natural birdie, your team's number flips high.
                    A natural eagle also doubles that hole. Turn off for straight scoring.
                  </span>
                </span>
              </label>
            </div>
          )}

          <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 4px' }}>
            Participants ({selectedPlayers.size} / {roundPlayers.length})
            {currentFormat.teamed && !currentFormat.vegas && selectedPlayers.size > 2 && (
              <span style={{ marginLeft: 6 }}>
                · Team A: {teamCounts.a} · Team B: {teamCounts.b}
              </span>
            )}
            {currentFormat.vegas && vegasTeamsMode === 'same' && (
              <span style={{ marginLeft: 6 }}>
                · Team A: {teamCounts.a} · Team B: {teamCounts.b}
              </span>
            )}
          </p>
          {currentFormat.vegas && vegasTeamsMode === 'switch6' && (
            <p style={{
              fontSize: '0.72rem', color: 'var(--gray-300)', margin: '0 0 6px',
              padding: '6px 8px', borderRadius: 4,
              background: 'rgba(212,185,122,0.08)', border: '1px solid var(--green-mid)',
            }}>
              Teams rotate automatically every {vegasSwitchBlock} holes — no assignment needed.
            </p>
          )}
          <div style={{ display: 'grid', gap: 4, marginBottom: 10, maxHeight: 160, overflowY: 'auto' }}>
            {roundPlayers.map(rp => {
              const on = selectedPlayers.has(rp.player_id)
              const team = teamAssignment.get(rp.player_id)
              return (
                <div
                  key={rp.player_id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 8px', borderRadius: 6,
                    background: on ? 'var(--green-dark)' : 'transparent',
                    border: '1px solid var(--green-mid)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer' }}>
                    <input type="checkbox" checked={on} onChange={() => togglePlayer(rp.player_id)} />
                    <span style={{ fontSize: '0.85rem' }}>{rp.name}</span>
                  </label>
                  {on && currentFormat.teamed && (
                    currentFormat.vegas
                      ? vegasTeamsMode === 'same'   // Vegas always needs A/B in 'same' — show from first selection.
                      : selectedPlayers.size > 2    // Other teamed formats keep the >2 rule (2-player pairs auto-split).
                  ) && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {['A', 'B'].map(t => (
                        <button
                          key={t}
                          onClick={() => setTeam(rp.player_id, t)}
                          style={{
                            padding: '2px 8px', borderRadius: 4,
                            border: `1px solid ${team === t ? 'var(--gold)' : 'var(--green-mid)'}`,
                            background: team === t ? 'var(--gold)' : 'transparent',
                            color: team === t ? 'var(--green-deep)' : 'var(--cream)',
                            fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {stubAckRequired && (
            <label
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                marginBottom: 8, padding: '8px 10px', borderRadius: 6,
                background: 'rgba(212,185,122,0.08)', border: '1px solid var(--gold)',
                fontSize: '0.8rem', cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={stubAgeAck}
                onChange={(e) => setStubAgeAck(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                I confirm every participant in this wager is 18 or older.
                <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.7rem', marginTop: 2 }}>
                  Required for money games (TournyFlex never holds funds).
                </span>
              </span>
            </label>
          )}

          {(() => {
            const selection = validateSideGameSelection({
              format,
              selectedCount: selectedPlayers.size,
              teamCounts,
              vegasTeamsMode,
              wolfRotationMode,
              wolfOrderLen: wolfRotationOrder.length,
            })
            const disabled = busy || !selection.valid || (stubAckRequired && !stubAgeAck)
            return (
              <>
                {!selection.valid && (
                  <p
                    role="status"
                    style={{
                      margin: '0 0 6px', padding: '6px 8px', borderRadius: 4,
                      background: 'rgba(229,115,115,0.12)', border: '1px solid var(--red, #e57373)',
                      color: 'var(--red, #e57373)', fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.35,
                    }}
                  >{selection.reason}</p>
                )}
                <button
                  onClick={createGame}
                  disabled={disabled}
                  style={{
                    width: '100%', padding: '10px', borderRadius: 6,
                    background: 'var(--gold)', color: 'var(--green-deep)',
                    border: 'none', fontWeight: 700, fontSize: '0.85rem',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  Start {currentFormat.label}
                </button>
              </>
            )
          })()}
        </div>
        </div>
      )}

      {!hideChrome && activeGames.length === 0 && !openForm && (
        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--gray-400)' }}>
          No side games yet on this round. Tap "+ New game" to start Skins, Nassau, Stableford, Simple, or Match Play.
        </p>
      )}

      {!hideChrome && games.map(g => {
        const active = g.participants.filter(p => !p.left_at)
        const activeIds = new Set(active.map(p => p.player_id))
        const notInGame = roundPlayers.filter(rp => !activeIds.has(rp.player_id))
        return (
          <div
            key={g.id}
            style={{
              marginTop: 8, padding: 8, borderRadius: 6,
              background: 'var(--green-dark)',
              border: '1px solid var(--green-mid)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '0.9rem' }}>
                  {g.format.replace('_', ' ')}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>
                  {active.length} player{active.length === 1 ? '' : 's'}
                  {g.ended_at ? ' · ended' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {(g.format === 'skins' || g.format === 'wolf' || g.format === 'shuffle_card') && (
                  <button
                    onClick={() => setExpandedGame(expandedGame === g.id ? null : g.id)}
                    style={{
                      padding: '4px 10px', border: '1px solid var(--gold)',
                      background: 'transparent', color: 'var(--gold)',
                      borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer',
                    }}
                  >
                    {expandedGame === g.id ? 'Hide' : 'View'}
                  </button>
                )}
                {!g.ended_at && (
                  <button
                    onClick={() => setEditingGame(editingGame?.game_id === g.id ? null : { game_id: g.id, format: g.format, config: g.config || {} })}
                    style={{
                      padding: '4px 10px', border: '1px solid var(--gold)',
                      background: 'transparent', color: 'var(--gold)',
                      borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer',
                    }}
                  >
                    {editingGame?.game_id === g.id ? 'Close' : 'Edit'}
                  </button>
                )}
                {!g.ended_at && (
                  <button
                    onClick={() => endGame(g.id)}
                    disabled={busy}
                    style={{
                      padding: '4px 10px', border: '1px solid var(--gray-500)',
                      background: 'transparent', color: 'var(--gray-300)',
                      borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer',
                    }}
                  >
                    End
                  </button>
                )}
              </div>
            </div>

            {/* F-094-T1 (review fix): participant chips with add/remove.
                Mirrors EventDraftAdmin so the standalone add_participant
                under-18 wager gate is exercisable from the app. */}
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              {active.map(p => (
                <span key={p.player_id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 999,
                  background: 'var(--green-deep)', fontSize: '0.72rem',
                }}>
                  {p.name}
                  {!g.ended_at && (
                    <button
                      onClick={() => removeParticipant(g.id, p.player_id)}
                      aria-label={`Remove ${p.name}`}
                      disabled={busy}
                      style={{
                        background: 'none', border: 'none', color: 'var(--gray-400)',
                        cursor: busy ? 'not-allowed' : 'pointer', padding: 0, fontSize: '0.85rem',
                      }}
                    >×</button>
                  )}
                </span>
              ))}
              {!g.ended_at && notInGame.length > 0 && (
                <select
                  value=""
                  onChange={(e) => addParticipant(g.id, e.target.value)}
                  disabled={busy}
                  aria-label="Add player to game"
                  style={{
                    fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4,
                    background: 'var(--green-deep)', color: 'var(--cream)',
                    border: '1px solid var(--green-mid)',
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  <option value="">+ add player</option>
                  {notInGame.map(rp => (
                    <option key={rp.player_id} value={rp.player_id}>{rp.name}</option>
                  ))}
                </select>
              )}
            </div>

            {expandedGame === g.id && g.format === 'skins' && (
              <div style={{ marginTop: 10, borderTop: '1px solid var(--green-mid)', paddingTop: 8 }}>
                <SkinsCard game_id={g.id} autoRefreshMs={0} />
              </div>
            )}
            {expandedGame === g.id && g.format === 'wolf' && (
              <div style={{ marginTop: 10, borderTop: '1px solid var(--green-mid)', paddingTop: 8 }}>
                <WolfCard
                  game_id={g.id}
                  viewerPlayerId={viewerPlayerId}
                  viewerCurrentHole={viewerCurrentHole}
                  autoRefreshMs={5000}
                />
              </div>
            )}
            {expandedGame === g.id && g.format === 'shuffle_card' && (
              <div style={{ marginTop: 10, borderTop: '1px solid var(--green-mid)', paddingTop: 8 }}>
                <ShuffleCard
                  game_id={g.id}
                  viewerPlayerId={viewerPlayerId}
                  viewerCurrentHole={viewerCurrentHole}
                  autoRefreshMs={5000}
                />
              </div>
            )}
            {editingGame?.game_id === g.id && (
              <EditGameForm
                game={g}
                config={editingGame.config}
                onChange={(config) => setEditingGame(e => ({ ...e, config }))}
                onSave={async (finalConfig) => {
                  setBusy(true)
                  try {
                    await callStandalone('update_game_config', {
                      game_id: g.id,
                      // F-101-T1: EditGameForm hands us the final config
                      // with stub_age_ack: true baked in when the 18+
                      // checkbox was ticked for a wager-flipping edit;
                      // fall back to the tracked config for older callers.
                      config: finalConfig || editingGame.config,
                    })
                    showToast('Game updated.')
                    setEditingGame(null)
                    await loadGames()
                  } catch (e) {
                    showToast(e.message)
                  }
                  setBusy(false)
                }}
                onCancel={() => setEditingGame(null)}
                busy={busy}
                roundPlayers={roundPlayers}
              />
            )}
          </div>
        )
      })}

      {/* F-094-T1 (review fix): hideChrome variant surfaces participant
          management inline below the scorecard so the standalone
          add_participant under-18 wager gate is testable in the
          scorecard flow (the pill strip navigates to /game/:gameId for
          per-format play; add/remove of players stays in the panel).
          Ended games and games with no roster to add stay hidden here. */}
      {hideChrome && activeGames.length > 0 && (
        <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
          {activeGames.map(g => {
            const active = g.participants.filter(p => !p.left_at)
            const activeIds = new Set(active.map(p => p.player_id))
            const notInGame = roundPlayers.filter(rp => !activeIds.has(rp.player_id))
            if (active.length === 0 && notInGame.length === 0) return null
            return (
              <div
                key={g.id}
                style={{
                  padding: 8, borderRadius: 6,
                  background: 'var(--green-dark)',
                  border: '1px solid var(--green-mid)',
                }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 6,
                }}>
                  <div style={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '0.8rem' }}>
                    {g.format.replace('_', ' ')} · players
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                  {active.map(p => (
                    <span key={p.player_id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 999,
                      background: 'var(--green-deep)', fontSize: '0.72rem',
                    }}>
                      {p.name}
                      <button
                        onClick={() => removeParticipant(g.id, p.player_id)}
                        aria-label={`Remove ${p.name}`}
                        disabled={busy}
                        style={{
                          background: 'none', border: 'none', color: 'var(--gray-400)',
                          cursor: busy ? 'not-allowed' : 'pointer', padding: 0, fontSize: '0.85rem',
                        }}
                      >×</button>
                    </span>
                  ))}
                  {notInGame.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => addParticipant(g.id, e.target.value)}
                      disabled={busy}
                      aria-label="Add player to game"
                      style={{
                        fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4,
                        background: 'var(--green-deep)', color: 'var(--cream)',
                        border: '1px solid var(--green-mid)',
                        cursor: busy ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="">+ add player</option>
                      {notInGame.map(rp => (
                        <option key={rp.player_id} value={rp.player_id}>{rp.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Toast surfaces server errors (including under_18_wager_block)
          in BOTH modes now — was previously gated to !hideChrome, which
          meant the scorecard flow silently dropped add-participant
          error copy. */}
      {toast && (
        <div style={{ marginTop: 8, padding: 6, background: 'var(--green-mid)', borderRadius: 4, fontSize: '0.75rem' }}>
          {toast}
        </div>
      )}

      <GameInfoModal format={infoFormat} onClose={() => setInfoFormat(null)} />
    </div>
  )
}

const miniBtn = {
  padding: '2px 8px', borderRadius: 4,
  background: 'transparent', color: 'var(--cream)',
  border: '1px solid var(--green-mid)',
  fontSize: '0.75rem', cursor: 'pointer',
}

// Pill-toggle style for the Vegas mode selectors.
function sgChip(active) {
  return {
    flex: 1, padding: '5px 6px', borderRadius: 4,
    border: `1px solid ${active ? 'var(--gold)' : 'var(--green-mid)'}`,
    background: active ? 'rgba(212,185,122,0.15)' : 'transparent',
    color: active ? 'var(--gold)' : 'var(--gray-300)',
    fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', lineHeight: 1.2,
  }
}

// Inline edit form for an active game. Which fields render depends on
// format:
//   * shuffle_card — mode picker (locked once cards have been drawn;
//                    server returns mode_locked and the toast tells the
//                    user).
//   * wolf         — rotation-order editor (locked once any hole state
//                    exists; server returns rotation_locked).
//   * everything else — unit_value only.
function EditGameForm({ game, config, onChange, onSave, onCancel, busy, roundPlayers }) {
  const format = game.format
  const setField = (key, value) => onChange({ ...config, [key]: value })
  const setUnit = (value) => {
    const num = Number(value)
    const safe = Number.isFinite(num) ? num : 0
    if (format === 'nassau') onChange({ ...config, bet_units: { front: safe, back: safe, overall: safe } })
    else if (format === 'match_1v1') onChange({ ...config, bet_units: safe })
    else onChange({ ...config, unit_value: safe })
  }
  const currentUnit = format === 'nassau'
    ? (config?.bet_units?.overall ?? 0)
    : format === 'match_1v1'
      ? (config?.bet_units ?? 0)
      : (config?.unit_value ?? 0)
  const wagerless = format === 'shuffle_card'

  // F-101-T1: age gate on the edit path. Mirror the create-form logic —
  // when the edit would flip the game from bragging → wager AND there
  // are participants who could be stubs (no birthdate on file), require
  // the same 18+ ack the create modal captures. roundPlayers doesn't
  // carry a birthdate signal, so we conservatively require the ack for
  // any wager-flipping edit with participants (casual rounds are stub-
  // heavy; over-ask, never under-ask). Server also gates on birthdate-
  // known minors so this is UX/audit — matches StandaloneGamesPanel
  // create-form pattern at :151.
  const priorUnit = format === 'nassau'
    ? (game?.config?.bet_units?.overall ?? 0)
    : format === 'match_1v1'
      ? (game?.config?.bet_units ?? 0)
      : (game?.config?.unit_value ?? 0)
  const wasWager = !wagerless && Number(priorUnit) > 0
  const nowWager = !wagerless && Number(currentUnit) > 0
  const flippedToWager = !wasWager && nowWager
  const activeParticipantCount = Array.isArray(game?.participants)
    ? game.participants.filter(p => !p.left_at).length
    : 0
  const stubAckRequired = flippedToWager && activeParticipantCount > 0
  const [stubAgeAck, setStubAgeAck] = useState(false)
  return (
    <div style={{
      marginTop: 10, borderTop: '1px solid var(--green-mid)',
      paddingTop: 10, display: 'grid', gap: 10,
    }}>
      {format === 'shuffle_card' && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 6px' }}>
            Mode (locked once cards have been drawn):
          </p>
          {[
            { v: 'straight_up', label: 'Straight Up' },
            { v: 'double_down', label: 'Double Down' },
            { v: 'duel',        label: 'Duel' },
          ].map(m => (
            <label key={m.v} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={(config?.shuffle_mode || 'straight_up') === m.v}
                onChange={() => setField('shuffle_mode', m.v)}
              />
              {m.label}
            </label>
          ))}
        </div>
      )}
      {format === 'wolf' && Array.isArray(config?.rotation_order) && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 6px' }}>
            Rotation order (locked once decisions have been made):
          </p>
          <div style={{ display: 'grid', gap: 4 }}>
            {config.rotation_order.map((pid, i) => {
              const name = roundPlayers.find(rp => rp.player_id === pid)?.name || pid.slice(0, 6)
              return (
                <div key={pid} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 8px', borderRadius: 4,
                  background: 'var(--green-dark)', border: '1px solid var(--green-mid)',
                  fontSize: '0.8rem',
                }}>
                  <span style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>{i + 1}.</span>
                  <span style={{ flex: 1 }}>{name}</span>
                  <button
                    disabled={i === 0}
                    onClick={() => {
                      const next = [...config.rotation_order]
                      ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
                      setField('rotation_order', next)
                    }}
                    style={miniBtn}
                  >↑</button>
                  <button
                    disabled={i === config.rotation_order.length - 1}
                    onClick={() => {
                      const next = [...config.rotation_order]
                      ;[next[i], next[i + 1]] = [next[i + 1], next[i]]
                      setField('rotation_order', next)
                    }}
                    style={miniBtn}
                  >↓</button>
                </div>
              )
            })}
          </div>
        </div>
      )}
      {format === 'vegas' && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 4px' }}>Ties:</p>
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { v: 'wash', label: 'Wash' },
                { v: 'push', label: 'Push (carries & doubles the next hole)' },
              ].map(m => (
                <button
                  key={m.v} type="button"
                  onClick={() => setField('ties_mode', m.v)}
                  style={sgChip((config?.ties_mode === 'push' ? 'push' : 'wash') === m.v)}
                >{m.label}</button>
              ))}
            </div>
          </div>
          {config?.teams_mode !== 'switch6' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config?.allow_press === true}
                onChange={(e) => setField('allow_press', e.target.checked)}
              />
              Allow Press — losing team can double a hole
            </label>
          )}
        </div>
      )}
      {!wagerless && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 6px' }}>
            Unit value:
          </p>
          <input
            type="number" min={0} step="0.25"
            value={currentUnit}
            onChange={e => setUnit(e.target.value)}
            style={{
              width: 120, padding: 6, borderRadius: 4,
              background: 'var(--green-deep)', color: 'var(--cream)',
              border: '1px solid var(--green-mid)', fontSize: '0.85rem',
            }}
          />
        </div>
      )}
      {stubAckRequired && (
        <label
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '8px 10px', borderRadius: 6,
            background: 'rgba(212,185,122,0.08)', border: '1px solid var(--gold)',
            fontSize: '0.8rem', cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={stubAgeAck}
            onChange={(e) => setStubAgeAck(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            I confirm every participant in this wager is 18 or older.
            <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.7rem', marginTop: 2 }}>
              Required to convert this bragging-rights game into a money game (TournyFlex never holds funds).
            </span>
          </span>
        </label>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 6,
            background: 'transparent', border: '1px solid var(--green-mid)',
            color: 'var(--cream)', fontSize: '0.85rem', cursor: 'pointer',
          }}
        >Cancel</button>
        <button
          onClick={() => {
            // F-101-T1: forward the ack flag on wager-flipping edits so
            // the server can rewrite it to the audit-trail object. Match
            // the create-path shape (config.stub_age_ack = true). Server
            // ignores this when stubs don't exist / merged config isn't
            // wager so it's safe to include unconditionally when ticked.
            const final = { ...config }
            if (stubAckRequired && stubAgeAck) {
              final.stub_age_ack = true
            }
            onSave(final)
          }}
          disabled={busy || (stubAckRequired && !stubAgeAck)}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 6,
            background: 'var(--gold)', border: 'none',
            color: 'var(--green-deep)', fontWeight: 700, fontSize: '0.85rem',
            cursor: (busy || (stubAckRequired && !stubAgeAck)) ? 'not-allowed' : 'pointer',
            opacity: (busy || (stubAckRequired && !stubAgeAck)) ? 0.6 : 1,
          }}
        >Save</button>
      </div>
    </div>
  )
}
